import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Box,
  FormHelperText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSnackbar } from 'notistack';
import { EmployeeProfile, EmployeeStatus } from '../../types/hr';
import {
  getEmployeeByUserId,
  createEmployeeForUser,
  updateEmployeeForUser,
} from '../../services/employees.ts';

// Validation schema
const employeeSchema = z.object({
  jobTitle: z.string().min(1, 'Job title is required'),
  employmentStartDate: z
    .string()
    .optional()
    .refine(
      (date) => !date || new Date(date) <= new Date(),
      'Employment start date cannot be in the future'
    ),
  status: z.enum(['ACTIVE', 'ON_LEAVE', 'LEFT']).optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactPhone: z
    .string()
    .optional()
    .refine(
      (phone) => !phone || phone === '' || /^\+4\d{9,12}$/.test(phone),
      'Emergency phone must be in format +4 followed by 9-12 digits'
    ),
  workSchedule: z.string().optional(),
  notes: z.string().max(2000, 'Notes cannot exceed 2000 characters').optional(),
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

interface HrProfileModalProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  userName?: string;
  initialProfile?: EmployeeProfile | null;
  onSuccess?: () => void;
}

const HrProfileModal: React.FC<HrProfileModalProps> = ({
  open,
  onClose,
  userId,
  userName,
  initialProfile,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [existingProfile, setExistingProfile] = useState<EmployeeProfile | null>(initialProfile || null);
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      jobTitle: '',
      employmentStartDate: '',
      status: 'ACTIVE',
      emergencyContactName: '',
      emergencyContactPhone: '',
      workSchedule: '',
      notes: '',
    },
  });

  // Use initial profile or fetch if not provided
  useEffect(() => {
    if (open && userId) {
      // If we have initial profile data, use it directly
      if (initialProfile !== undefined) {
        setExistingProfile(initialProfile);
        if (initialProfile) {
          // Populate form with existing data
          reset({
            jobTitle: initialProfile.jobTitle || '',
            employmentStartDate: initialProfile.employmentStartDate
              ? initialProfile.employmentStartDate.split('T')[0]
              : '',
            status: initialProfile.status || 'ACTIVE',
            emergencyContactName: initialProfile.emergencyContactName || '',
            emergencyContactPhone: initialProfile.emergencyContactPhone || '',
            workSchedule: initialProfile.workSchedule || '',
            notes: initialProfile.notes || '',
          });
        } else {
          // Reset to defaults for new profile
          reset({
            jobTitle: '',
            employmentStartDate: '',
            status: 'ACTIVE',
            emergencyContactName: '',
            emergencyContactPhone: '',
            workSchedule: '',
            notes: '',
          });
        }
      } else {
        // Fallback to fetching if no initial data provided
        setFetchingProfile(true);
        getEmployeeByUserId(userId)
          .then((profile) => {
            setExistingProfile(profile);
            if (profile) {
              reset({
                jobTitle: profile.jobTitle || '',
                employmentStartDate: profile.employmentStartDate
                  ? profile.employmentStartDate.split('T')[0]
                  : '',
                status: profile.status || 'ACTIVE',
                emergencyContactName: profile.emergencyContactName || '',
                emergencyContactPhone: profile.emergencyContactPhone || '',
                workSchedule: profile.workSchedule || '',
                notes: profile.notes || '',
              });
            } else {
              reset({
                jobTitle: '',
                employmentStartDate: '',
                status: 'ACTIVE',
                emergencyContactName: '',
                emergencyContactPhone: '',
                workSchedule: '',
                notes: '',
              });
            }
          })
          .catch((error) => {
            console.error('Error fetching employee profile:', error);
            enqueueSnackbar('Failed to load HR profile', { variant: 'error' });
          })
          .finally(() => {
            setFetchingProfile(false);
          });
      }
    }
  }, [open, userId, initialProfile, reset, enqueueSnackbar]);

  const onSubmit = async (data: EmployeeFormData) => {
    setLoading(true);
    try {
      if (existingProfile?.id) {
        // Update existing profile
        await updateEmployeeForUser(existingProfile.id, {
          ...data,
          userId,
        });
        enqueueSnackbar('HR profile updated successfully', { variant: 'success' });
      } else {
        // Create new profile
        await createEmployeeForUser(userId, {
          ...data,
          userId,
        });
        enqueueSnackbar('HR profile created successfully', { variant: 'success' });
      }
      
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error saving employee profile:', error);
      enqueueSnackbar(
        error.message || 'Failed to save HR profile',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const statusOptions: EmployeeStatus[] = ['ACTIVE', 'ON_LEAVE', 'LEFT'];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        HR Profile for {existingProfile?.userFullName || userName || `User #${userId}`}
      </DialogTitle>
      <DialogContent>
        {fetchingProfile ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" sx={{ mt: 2 }}>
            <Controller
              name="jobTitle"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Job Title"
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors.jobTitle}
                  helperText={errors.jobTitle?.message}
                />
              )}
            />

            <Controller
              name="employmentStartDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Employment Start Date"
                  type="date"
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.employmentStartDate}
                  helperText={errors.employmentStartDate?.message}
                />
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select {...field} label="Status">
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="emergencyContactName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Emergency Contact Name"
                  fullWidth
                  margin="normal"
                />
              )}
            />

            <Controller
              name="emergencyContactPhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Emergency Contact Phone"
                  fullWidth
                  margin="normal"
                  placeholder="+40712345678"
                  error={!!errors.emergencyContactPhone}
                  helperText={
                    errors.emergencyContactPhone?.message ||
                    'Format: +4 followed by 9-12 digits'
                  }
                />
              )}
            />

            <Controller
              name="workSchedule"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Work Schedule"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  placeholder="e.g., Full-time, Monday-Friday 9-17"
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              )}
            />

            {existingProfile && (
              <Box mt={2}>
                <Alert severity="info">
                  Last updated: {new Date(existingProfile.updatedAt || existingProfile.createdAt || '').toLocaleString()}
                </Alert>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading || isSubmitting || fetchingProfile}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HrProfileModal;
